import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { cacheService } from './cacheService';

interface CartonContentReceivingRequest {
  cartonId: number;
  contentCode: string;
  projectId: number;
}

interface CartonContentReceivingResponse {
  success: boolean;
  message?: string;
  panels: {
    received: number;
    notReceived: number;
    total: number;
  };
  hardware: {
    received: number;
    notReceived: number;
    total: number;
  };
}

export class ApiService {
  private static instance: ApiService;
  private api: AxiosInstance;

  private constructor() {
    // Use relative path for development (proxy will handle it) or full URL for production
    const baseURL = process.env.NODE_ENV === 'production' 
      ? (process.env.REACT_APP_API_URL || '/api')
      : '/api';
    
    // Ensure baseURL ends with /api
    const normalizedBaseURL = baseURL.endsWith('/api') ? baseURL : `${baseURL}/api`;

    this.api = axios.create({
      baseURL: normalizedBaseURL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // Add request interceptor
    this.api.interceptors.request.use(
      async (config) => {
        const auth = await cacheService.getCachedAuth();
        if (auth?.token && config.headers) {
          config.headers.Authorization = `Bearer ${auth.token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          await cacheService.clearAuth();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.get<T>(url, config);
    return response.data;
  }

  public async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post<T>(url, data, config);
    return response.data;
  }

  public async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.put<T>(url, data, config);
    return response.data;
  }

  public async delete<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.delete<T>(url, { ...config, data });
    return response.data;
  }

  // Project related endpoints
  public async getProjects() {
    return this.get<Project[]>('/projects');
  }

  public async getProjectDetailed(projectId: string) {
    try {
      const response = await this.api.get(`/projects/${projectId}/detailed`);
      return response.data;
    } catch (error) {
      console.error('Error fetching detailed project:', error);
      throw error;
    }
  }

  // Cache management methods
  private getProjectCacheKey(projectId: string): string {
    return `project:${projectId}:detailed`;
  }

  async getCachedProject(projectId: string) {
    const cacheKey = this.getProjectCacheKey(projectId);
    const cachedData = localStorage.getItem(cacheKey);
    
    if (cachedData) {
      const { data, timestamp } = JSON.parse(cachedData);
      // Check if cache is still valid (15 minutes)
      if (Date.now() - timestamp < 15 * 60 * 1000) {
        return data;
      }
    }
    
    return null;
  }

  async cacheProject(projectId: string, data: any) {
    const cacheKey = this.getProjectCacheKey(projectId);
    const cacheData = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  }

  async getProjectWithCache(projectId: string) {
    // Try to get from cache first
    const cachedData = await this.getCachedProject(projectId);
    if (cachedData) {
      return cachedData;
    }

    // If not in cache, fetch from API
    const response = await this.getProjectDetailed(projectId);
    
    // Cache the response
    if (response.status === 'success') {
      await this.cacheProject(projectId, response.data);
    }

    return response.data;
  }

  // Carton related endpoints
  public async validateCarton(code: string, projectId: string) {
    return this.post<ProjectCarton>('/cartons/validate', { code, project_id: projectId });
  }

  public async scanCartonContent(code: string, cartonId: string, projectId: string) {
    return this.post<ScanResult>(`/cartons/${cartonId}/scan`, { 
      code, 
      project_id: projectId,
      scanned_at: new Date().toISOString()
    });
  }

  // Activity logging
  public async logActivity(activity: {
    type: string;
    project_id: string;
    carton_id: string;
    content_code: string;
    user_id: string;
    status: string;
  }) {
    return this.post('/activities', activity);
  }

  async getProjectCartons(projectId: number) {
    const response = await this.api.get(`/projects/${projectId}/cartons`);
    return response.data;
  }

  async receiveCartonContent(data: CartonContentReceivingRequest): Promise<{ data: CartonContentReceivingResponse }> {
    const response = await this.api.post(`/cartons/${data.cartonId}/content`, {
      content_code: data.contentCode,
      project_id: data.projectId
    });
    return response;
  }
}

export const apiService = ApiService.getInstance(); 